import { useApiFetch } from '~/composables/useApiFetch';
import type {
  ArchiveNotificationsRequest,
  ArchiveNotificationsResponse,
  DeleteNotificationsRequest,
  DeleteNotificationsResponse,
  GetNotificationsParams,
  MarkReadRequest,
  MarkReadResponse,
  NotificationListResponse,
  NotificationSettings,
  UnreadCountResponse,
  UpdateNotificationSettingsRequest,
} from '~/types/notifications';

/**
 * 獲取通知列表
 */
export async function getNotificationsApi(
  params: GetNotificationsParams
): Promise<NotificationListResponse> {
  const queryParams = new URLSearchParams();
  queryParams.append('platform', params.platform);
  if (params.type) queryParams.append('type', params.type);
  if (params.is_read !== undefined) queryParams.append('is_read', String(params.is_read));
  if (params.is_archived !== undefined)
    queryParams.append('is_archived', String(params.is_archived));
  if (params.page) queryParams.append('page', String(params.page));
  if (params.page_size) queryParams.append('page_size', String(params.page_size));

  const response = await useApiFetch(`/notifications?${queryParams.toString()}`, {
    method: 'GET',
    requireAuth: true,
  });

  // API 回傳格式：{ code, message, data: { notifications, pagination } }
  const result: { data: NotificationListResponse } = await response.json();
  return result.data;
}

/**
 * 獲取未讀通知數量
 */
export async function getUnreadCountApi(platform: string): Promise<UnreadCountResponse> {
  const response = await useApiFetch(`/notifications/unread-count?platform=${platform}`, {
    method: 'GET',
    requireAuth: true,
  });

  // API 回傳格式：{ code, message, data: { unread_count } }
  const result: { data: UnreadCountResponse } = await response.json();
  return result.data;
}

/**
 * 標記通知為已讀
 */
export async function markNotificationsReadApi(
  payload: MarkReadRequest
): Promise<MarkReadResponse> {
  const response = await useApiFetch('/notifications/read', {
    method: 'POST',
    body: JSON.stringify(payload),
    requireAuth: true,
  });

  // API 回傳格式：{ code, message, data: { ... } }
  const result: { data: MarkReadResponse } = await response.json();
  return result.data;
}

/**
 * 刪除通知
 */
export async function deleteNotificationsApi(
  payload: DeleteNotificationsRequest
): Promise<DeleteNotificationsResponse> {
  const response = await useApiFetch('/notifications', {
    method: 'DELETE',
    body: JSON.stringify(payload),
    requireAuth: true,
  });

  // API 回傳格式：{ code, message, data: { ... } }
  const result: { data: DeleteNotificationsResponse } = await response.json();
  return result.data;
}

/**
 * 歸檔通知
 */
export async function archiveNotificationsApi(
  payload: ArchiveNotificationsRequest
): Promise<ArchiveNotificationsResponse> {
  const response = await useApiFetch('/notifications/archive', {
    method: 'POST',
    body: JSON.stringify(payload),
    requireAuth: true,
  });

  // API 回傳格式：{ code, message, data: { ... } }
  const result: { data: ArchiveNotificationsResponse } = await response.json();
  return result.data;
}

/**
 * 獲取通知設置
 */
export async function getNotificationSettingsApi(platform: string): Promise<NotificationSettings> {
  const response = await useApiFetch(`/notifications/settings?platform=${platform}`, {
    method: 'GET',
    requireAuth: true,
  });

  // API 回傳格式：{ code, message, data: { ... } }
  const result: { data: NotificationSettings } = await response.json();
  return result.data;
}

/**
 * 更新通知設置
 */
export async function updateNotificationSettingsApi(
  payload: UpdateNotificationSettingsRequest
): Promise<NotificationSettings> {
  const response = await useApiFetch('/notifications/settings', {
    method: 'PUT',
    body: JSON.stringify(payload),
    requireAuth: true,
  });

  // API 回傳格式：{ code, message, data: { ... } }
  const result: { data: NotificationSettings } = await response.json();
  return result.data;
}
